import { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { apiService } from '@vsat/shared/lib/api';

interface AiGradingWaitProps {
  gradingId: string;
  onCompleted: (result: any) => void;
  onFailed: () => void;
}

const POLL_INTERVAL_MS = 3000;
const TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

export function AiGradingWait({ gradingId, onCompleted, onFailed }: AiGradingWaitProps) {
  const [status, setStatus] = useState<'loading' | 'failed' | 'timeout'>('loading');

  const startPolling = useCallback(() => {
    let interval: ReturnType<typeof setInterval>;
    let timeout: ReturnType<typeof setTimeout>;
    let resolved = false;

    const poll = async () => {
      try {
        const result = await apiService.getAiGradingResult(gradingId);
        if (resolved) return;
        if (result.status === 'COMPLETED') {
          resolved = true;
          clearInterval(interval);
          clearTimeout(timeout);
          onCompleted(result);
        } else if (result.status === 'FAILED') {
          resolved = true;
          clearInterval(interval);
          clearTimeout(timeout);
          setStatus('failed');
          onFailed();
        }
      } catch {
        // Continue polling on error
      }
    };

    interval = setInterval(poll, POLL_INTERVAL_MS);
    timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        clearInterval(interval);
        setStatus('timeout');
        onFailed();
      }
    }, TIMEOUT_MS);

    poll(); // Initial call
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [gradingId, onCompleted, onFailed]);

  useEffect(() => {
    const cleanup = startPolling();
    return cleanup;
  }, [startPolling]);

  if (status === 'failed' || status === 'timeout') {
    return (
      <div className='min-h-[60vh] flex items-center justify-center'>
        <div className='text-center max-w-md mx-auto p-8'>
          <div className='w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4'>
            <AlertCircle className='w-8 h-8 text-red-600' />
          </div>
          <h2 className='text-xl font-bold text-gray-900 mb-2'>
            {status === 'timeout' ? 'Chấm bài quá lâu' : 'Lỗi chấm bài'}
          </h2>
          <p className='text-gray-500 mb-6'>
            {status === 'timeout'
              ? 'Quá trình chấm bài tự luận mất nhiều thời gian hơn dự kiến.'
              : 'Đã xảy ra lỗi khi chấm bài tự luận.'}
          </p>
          <button
            onClick={() => {
              setStatus('loading');
              startPolling();
            }}
            className='inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors'
          >
            <RotateCcw className='w-5 h-5' />
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-[60vh] flex items-center justify-center'>
      <div className='text-center max-w-md mx-auto p-8'>
        <div className='w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6 animate-pulse'>
          <span className='text-3xl'>✍️</span>
        </div>
        <h2 className='text-xl font-bold text-gray-900 mb-2'>Đang chấm bài tự luận...</h2>
        <p className='text-gray-500 mb-6'>
          AI đang đọc và chấm điểm các câu tự luận của bạn. Quá trình này có thể mất từ 10-60 giây.
        </p>
        <div className='flex items-center justify-center gap-2 text-sm text-gray-400'>
          <Loader2 className='w-4 h-4 animate-spin' />
          <span>Vui lòng không rời trang</span>
        </div>
      </div>
    </div>
  );
}
